import * as pc from 'playcanvas';
import type { ArtworkPlacement, FrameType, GalleryArtwork, GallerySceneSelection } from './types';

interface GallerySceneOptions {
  canvas: HTMLCanvasElement;
  artworks?: GalleryArtwork[];
  builderMode?: boolean;
  previewEnabled?: boolean;
  sceneVariant?: SceneVariant;
  displayArtworks?: boolean;
  onSelect: (selection: GallerySceneSelection | null) => void;
  onPlace?: (placement: ArtworkPlacement) => void;
  onMove: (position: { x: number; z: number; yaw: number }) => void;
}

interface ArtworkObject {
  artwork: GalleryArtwork;
  entity: pc.Entity;
  position: pc.Vec3;
  kind: FrameType;
}

type Slot = {
  x: number;
  z: number;
  rotationY: number;
  kind: FrameType;
};

type LightFixture = {
  name: string;
  position: [number, number, number];
  target: [number, number, number];
  color: string;
  intensity: number;
  range: number;
  innerConeAngle: number;
  outerConeAngle: number;
};

type CollisionRect = {
  x: number;
  z: number;
  halfX: number;
  halfZ: number;
  rotationY?: number;
};

type CollisionCircle = {
  x: number;
  z: number;
  radius: number;
};

type SceneVariant = 'rdfz' | 'secret';

const visitorNames = ['Ethan', 'Olivia', 'Sophia', 'Lucas', 'Mia', 'Noah'];
const radToDeg = 180 / Math.PI;
const degToRad = Math.PI / 180;
const artworkWallOffset = 0.36;
const venueAssetUrls: Record<SceneVariant, string> = {
  rdfz: '/assets/rdfz-gallery-venue-v1.glb',
  secret: '/assets/artchais-gallery-venue-v1.glb',
};
const rdfzVenueMetrics = {
  floorWidth: 22.4,
  floorDepth: 32.8,
  ceilingY: 3.28,
  hangY: 2.35,
  outerWallX: 10.45,
  outerWallZ: 11.55,
};
type RdfzWallSurface = {
  anchorX: number;
  anchorZ: number;
  rotationY: number;
  tangent: 'x' | 'z';
  min: number;
  max: number;
};
const rdfzWallSurfaces: RdfzWallSurface[] = [
  { anchorX: 0, anchorZ: -rdfzVenueMetrics.outerWallZ, rotationY: 0, tangent: 'x', min: -9.2, max: 9.2 },
  { anchorX: 0, anchorZ: rdfzVenueMetrics.outerWallZ, rotationY: Math.PI, tangent: 'x', min: -9.2, max: 9.2 },
  { anchorX: -rdfzVenueMetrics.outerWallX, anchorZ: 0, rotationY: Math.PI / 2, tangent: 'z', min: -10.8, max: 10.8 },
  { anchorX: rdfzVenueMetrics.outerWallX, anchorZ: 0, rotationY: -Math.PI / 2, tangent: 'z', min: -10.8, max: 10.8 },
  { anchorX: 0, anchorZ: -3.8, rotationY: 0, tangent: 'x', min: -7.8, max: 7.8 },
  { anchorX: 0, anchorZ: 7.8, rotationY: Math.PI, tangent: 'x', min: -7.8, max: 7.8 },
];
const secretCollisionRects: CollisionRect[] = [
  { x: -11.4, z: -4.1, halfX: 0.42, halfZ: 12.65 },
  { x: 11.4, z: -4.1, halfX: 0.42, halfZ: 12.65 },
  { x: -10.2, z: 9.2, halfX: 6.75, halfZ: 0.42 },
  { x: 10.2, z: 9.2, halfX: 6.75, halfZ: 0.42 },
  { x: 0, z: -10.8, halfX: 9.2, halfZ: 0.44 },
  { x: -22.2, z: 5.2, halfX: 6.25, halfZ: 0.42, rotationY: -0.44 },
  { x: 22.2, z: 5.2, halfX: 6.25, halfZ: 0.42, rotationY: 0.44 },
  { x: -2.85, z: 12.6, halfX: 0.32, halfZ: 0.32 },
  { x: 2.85, z: 12.6, halfX: 0.32, halfZ: 0.32 },
];
const secretCollisionCircles: CollisionCircle[] = [
  { x: 0, z: -2.2, radius: 7.65 },
  { x: -26, z: -17.6, radius: 0.52 },
  { x: 26, z: -17.6, radius: 0.52 },
  { x: -26, z: 15.8, radius: 0.52 },
  { x: 26, z: 15.8, radius: 0.52 },
  { x: -6.8, z: -2.2, radius: 0.52 },
  { x: 6.8, z: -2.2, radius: 0.52 },
  { x: -15.8, z: 10.8, radius: 0.52 },
  { x: 15.8, z: 10.8, radius: 0.52 },
];
const indoorFixtures: LightFixture[] = [
  { name: 'entry_linear_left', position: [-2.6, 6.35, 14.2], target: [-1.2, 1.8, 9.6], color: '#fff0d0', intensity: 2.35, range: 13, innerConeAngle: 24, outerConeAngle: 42 },
  { name: 'entry_linear_right', position: [2.6, 6.35, 14.2], target: [1.2, 1.8, 9.6], color: '#fff0d0', intensity: 2.35, range: 13, innerConeAngle: 24, outerConeAngle: 42 },
  { name: 'atrium_softbox_front', position: [0, 7.1, 8.4], target: [0, 0.9, -1.2], color: '#ffe6bd', intensity: 3.2, range: 18, innerConeAngle: 34, outerConeAngle: 62 },
  { name: 'atrium_softbox_rear', position: [0, 7.1, -12.6], target: [0, 0.9, -2.2], color: '#ffe6bd', intensity: 3.0, range: 18, innerConeAngle: 34, outerConeAngle: 62 },
  { name: 'left_wall_washer_a', position: [-10.6, 5.95, -13.2], target: [-11.1, 2.35, -12.8], color: '#ffddb2', intensity: 2.2, range: 9, innerConeAngle: 18, outerConeAngle: 34 },
  { name: 'left_wall_washer_b', position: [-10.6, 5.95, -4.2], target: [-11.1, 2.35, -4.2], color: '#ffddb2', intensity: 2.2, range: 9, innerConeAngle: 18, outerConeAngle: 34 },
  { name: 'left_wall_washer_c', position: [-10.6, 5.95, 4.8], target: [-11.1, 2.35, 4.6], color: '#ffddb2', intensity: 2.2, range: 9, innerConeAngle: 18, outerConeAngle: 34 },
  { name: 'right_wall_washer_a', position: [10.6, 5.95, -13.2], target: [11.1, 2.35, -12.8], color: '#ffddb2', intensity: 2.2, range: 9, innerConeAngle: 18, outerConeAngle: 34 },
  { name: 'right_wall_washer_b', position: [10.6, 5.95, -4.2], target: [11.1, 2.35, -4.2], color: '#ffddb2', intensity: 2.2, range: 9, innerConeAngle: 18, outerConeAngle: 34 },
  { name: 'right_wall_washer_c', position: [10.6, 5.95, 4.8], target: [11.1, 2.35, 4.6], color: '#ffddb2', intensity: 2.2, range: 9, innerConeAngle: 18, outerConeAngle: 34 },
  { name: 'rear_monumental_a', position: [-12.5, 6.2, -20.1], target: [-12.5, 2.75, -21], color: '#ffe1b8', intensity: 2.5, range: 10, innerConeAngle: 18, outerConeAngle: 32 },
  { name: 'rear_monumental_b', position: [0, 6.2, -20.1], target: [0, 2.75, -21], color: '#ffe1b8', intensity: 2.5, range: 10, innerConeAngle: 18, outerConeAngle: 32 },
  { name: 'rear_monumental_c', position: [12.5, 6.2, -20.1], target: [12.5, 2.75, -21], color: '#ffe1b8', intensity: 2.5, range: 10, innerConeAngle: 18, outerConeAngle: 32 },
];

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function color(hex: string, alpha = 1) {
  const clean = hex.replace('#', '');
  const value = Number.parseInt(clean, 16);
  return new pc.Color(
    ((value >> 16) & 255) / 255,
    ((value >> 8) & 255) / 255,
    (value & 255) / 255,
    alpha,
  );
}

function distance2d(a: pc.Vec3, b: pc.Vec3) {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dz * dz);
}

function setStandardMaterial(entity: pc.Entity, material: pc.StandardMaterial) {
  if (entity.render) entity.render.material = material;
}

function eulerYFromRadians(radians: number) {
  return radians * radToDeg;
}

export class GalleryScene {
  private readonly app: pc.Application;
  private readonly camera: pc.Entity;
  private readonly artworkLayer: pc.Entity;
  private readonly artworkLightLayer: pc.Entity;
  private readonly previewLayer: pc.Entity;
  private readonly artworkObjects: ArtworkObject[] = [];
  private readonly keys = new Set<string>();
  private readonly builderMode: boolean;
  private readonly previewEnabled: boolean;
  private readonly displayArtworks: boolean;
  private readonly onSelect: GallerySceneOptions['onSelect'];
  private readonly onPlace?: GallerySceneOptions['onPlace'];
  private readonly onMove: GallerySceneOptions['onMove'];
  private readonly materials: Record<string, pc.StandardMaterial>;
  private readonly sceneVariant: SceneVariant;
  private readonly textureCache = new Map<string, pc.Texture>();
  private readonly textureInflight = new Map<string, Promise<pc.Texture>>();
  private artworkCatalog: GalleryArtwork[] = [];
  private previewArtworkId = '';
  private previewRotationOffset = 0;
  private venueEntity?: pc.Entity;
  private protagonist?: pc.Entity;
  private playerPosition = new pc.Vec3(0, 0, 16.2);
  private yaw = 0;
  private pitch = 0.16;
  private dragging = false;
  private lastPointer = { x: 0, y: 0 };
  private focusedArtworkId = '';
  private disposed = false;

  constructor(options: GallerySceneOptions) {
    this.app = new pc.Application(options.canvas, {
      graphicsDeviceOptions: {
        antialias: true,
        alpha: false,
        powerPreference: 'high-performance',
      },
    });
    this.app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW);
    this.app.setCanvasResolution(pc.RESOLUTION_AUTO);

    this.sceneVariant = options.sceneVariant || 'rdfz';
    this.app.scene.ambientLight = this.sceneVariant === 'rdfz' ? color('#c9d3dc') : color('#928775');
    this.app.scene.exposure = this.sceneVariant === 'rdfz' ? 0.92 : 1.12;
    this.onSelect = options.onSelect;
    this.onPlace = options.onPlace;
    this.onMove = options.onMove;
    this.builderMode = Boolean(options.builderMode);
    this.previewEnabled = Boolean(options.previewEnabled);
    this.displayArtworks = options.displayArtworks ?? true;
    if (this.sceneVariant === 'rdfz') {
      this.playerPosition.set(-7.2, 0, 5.8);
      this.yaw = -0.62;
      this.pitch = 0.0;
    }
    this.materials = this.createMaterials();
    this.camera = new pc.Entity('gallery_camera', this.app);
    this.artworkLayer = new pc.Entity('artwork_layer', this.app);
    this.artworkLightLayer = new pc.Entity('artwork_light_layer', this.app);
    this.previewLayer = new pc.Entity('placement_preview_layer', this.app);

    this.camera.addComponent('camera', {
      fov: 62,
      nearClip: 0.08,
      farClip: 120,
      clearColor: this.sceneVariant === 'rdfz' ? color('#d9e5f0') : color('#242624'),
    });
    this.camera.setPosition(this.sceneVariant === 'rdfz' ? -7.2 : 0, this.sceneVariant === 'rdfz' ? 1.72 : 2.55, this.sceneVariant === 'rdfz' ? 5.8 : 18.6);
    this.camera.setEulerAngles(this.pitch * radToDeg, this.yaw * radToDeg, 0);

    this.app.root.addChild(this.camera);
    this.app.root.addChild(this.artworkLayer);
    this.app.root.addChild(this.artworkLightLayer);
    this.app.root.addChild(this.previewLayer);

    this.buildBaseLighting();
    this.loadVenueAsset();
    if (this.builderMode) this.buildProtagonist();
    if (this.sceneVariant !== 'rdfz') this.buildVisitors();
    if (options.artworks?.length) this.setArtworks(options.artworks, [], !this.builderMode);
    this.bindEvents(options.canvas);
    this.app.on('update', this.update);
    this.app.start();
  }

  setArtworks(artworks: GalleryArtwork[], placements: ArtworkPlacement[] = [], autoPlace = !this.builderMode) {
    this.artworkCatalog = artworks;
    if (!this.displayArtworks) {
      this.artworkObjects.splice(0).forEach((item) => item.entity.destroy());
      this.artworkLightLayer.children.slice().forEach((child) => child.destroy());
      this.previewLayer.children.slice().forEach((child) => child.destroy());
      this.focusedArtworkId = '';
      this.onSelect(null);
      return;
    }
    this.placeArtworks(artworks, placements, autoPlace);
    if (this.previewEnabled && !this.previewArtworkId && artworks[0]) this.setPreviewArtwork(artworks[0].id);
  }

  setPreviewArtwork(artworkId: string) {
    this.previewArtworkId = artworkId;
    this.previewRotationOffset = 0;
    this.rebuildPlacementPreview();
  }

  rotatePlacementPreview(direction = 1) {
    if (!this.builderMode || !this.previewEnabled) return;
    this.previewRotationOffset += direction * (Math.PI / 2);
    this.updatePlacementPreview();
  }

  getArtworkPlacements(): ArtworkPlacement[] {
    return this.artworkObjects.map((item) => ({
      id: this.getPlacementId(item.entity) || item.artwork.id,
      artworkId: item.artwork.id,
      title: item.artwork.title,
      x: Number(item.position.x.toFixed(2)),
      y: Number(item.position.y.toFixed(2)),
      z: Number(item.position.z.toFixed(2)),
      rotationY: Number((item.entity.getEulerAngles().y * degToRad).toFixed(3)),
      scale: Number(item.entity.getLocalScale().x.toFixed(3)),
      kind: item.kind,
      createdIn: 'playcanvas',
    }));
  }

  updateArtworkPlacement(id: string, patch: Partial<ArtworkPlacement>) {
    const item = this.findArtworkByPlacementId(id);
    if (!item) return;
    const rotationY = patch.rotationY ?? item.entity.getEulerAngles().y * degToRad;
    const logicalPosition = new pc.Vec3(
      patch.x ?? item.position.x,
      patch.y ?? item.position.y,
      patch.z ?? item.position.z,
    );
    this.setArtworkTransform(item.entity, logicalPosition, rotationY);
    if (patch.scale !== undefined) item.entity.setLocalScale(patch.scale, patch.scale, patch.scale);
    if (patch.kind) item.kind = patch.kind;
    item.position.copy(logicalPosition);
    this.emitSelection(item);
  }

  addFrameFromArtwork(artworkId: string): ArtworkPlacement | null {
    const artwork = this.artworkCatalog.find((item) => item.id === artworkId);
    if (!artwork) return null;

    const placement = {
      ...this.makePreviewPlacement(artwork),
      id: `${artwork.id}-${Date.now().toString(36)}`,
      artworkId: artwork.id,
      title: artwork.title,
      createdIn: 'playcanvas' as const,
    };

    this.addPlacement(placement);
    this.updatePlacementPreview();
    return placement;
  }

  removeArtworkPlacement(id: string) {
    const index = this.artworkObjects.findIndex((item) => this.getPlacementId(item.entity) === id);
    if (index === -1) return;
    const [item] = this.artworkObjects.splice(index, 1);
    item.entity.destroy();
  }

  dispose() {
    this.disposed = true;
    this.app.off('update', this.update);
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('resize', this.handleResize);
    this.app.graphicsDevice.canvas.removeEventListener('pointerdown', this.handlePointerDown);
    this.app.graphicsDevice.canvas.removeEventListener('pointermove', this.handlePointerMove);
    this.app.graphicsDevice.canvas.removeEventListener('pointerup', this.handlePointerUp);
    this.app.graphicsDevice.canvas.removeEventListener('click', this.handleClick);
    this.app.graphicsDevice.canvas.removeEventListener('contextmenu', this.handleContextMenu);
    this.textureInflight.clear();
    this.textureCache.forEach((texture) => texture.destroy());
    this.textureCache.clear();
    this.app.destroy();
  }

  private createSurfaceTexture(name: string, base: string, line: string, mode: 'concrete' | 'rubber' | 'brushed', size = 256) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, size, size);

    let seed = name.split('').reduce((total, letter) => total + letter.charCodeAt(0), 17);
    const rand = () => {
      seed = (seed * 1664525 + 1013904223) % 4294967296;
      return seed / 4294967296;
    };

    if (mode === 'concrete') {
      for (let i = 0; i < 5200; i += 1) {
        const alpha = 0.03 + rand() * 0.08;
        ctx.fillStyle = rand() > 0.52 ? `rgba(255,255,255,${alpha})` : `rgba(0,0,0,${alpha})`;
        ctx.fillRect(rand() * size, rand() * size, 0.55 + rand() * 1.8, 0.55 + rand() * 1.8);
      }
      ctx.strokeStyle = line;
      ctx.globalAlpha = 0.08;
      for (let i = 0; i < 18; i += 1) {
        ctx.beginPath();
        ctx.moveTo(rand() * size, rand() * size);
        ctx.lineTo(rand() * size, rand() * size);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    } else if (mode === 'rubber') {
      ctx.strokeStyle = line;
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.18;
      for (let y = -size; y < size * 2; y += 7) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(size, y + size * 0.32);
        ctx.stroke();
      }
      ctx.globalAlpha = 0.08;
      for (let i = 0; i < 3000; i += 1) {
        ctx.fillStyle = rand() > 0.55 ? '#ffffff' : '#000000';
        ctx.fillRect(rand() * size, rand() * size, 1, 1);
      }
      ctx.globalAlpha = 1;
    } else {
      ctx.strokeStyle = line;
      ctx.globalAlpha = 0.22;
      for (let y = 0; y < size; y += 3) {
        ctx.beginPath();
        ctx.moveTo(0, y + rand() * 1.5);
        ctx.lineTo(size, y + rand() * 1.5);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    const texture = new pc.Texture(this.app.graphicsDevice, {
      name,
      width: size,
      height: size,
      mipmaps: true,
      minFilter: pc.FILTER_LINEAR_MIPMAP_LINEAR,
      magFilter: pc.FILTER_LINEAR,
      addressU: pc.ADDRESS_REPEAT,
      addressV: pc.ADDRESS_REPEAT,
    });
    texture.setSource(canvas);
    return texture;
  }

  private createNormalTexture(name: string, mode: 'concrete' | 'rubber' | 'brushed', size = 256) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const image = ctx.createImageData(size, size);
    let seed = name.split('').reduce((total, letter) => total + letter.charCodeAt(0), 23);
    const rand = () => {
      seed = (seed * 1103515245 + 12345) % 2147483648;
      return seed / 2147483648;
    };

    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        const i = (y * size + x) * 4;
        const grain = (rand() - 0.5) * (mode === 'concrete' ? 20 : 9);
        const stripe = mode === 'rubber' ? Math.sin((x + y * 0.22) * 0.45) * 8 : 0;
        const brush = mode === 'brushed' ? Math.sin(y * 1.9) * 10 : 0;
        image.data[i] = 128 + grain + stripe;
        image.data[i + 1] = 128 + grain + brush;
        image.data[i + 2] = 238;
        image.data[i + 3] = 255;
      }
    }
    ctx.putImageData(image, 0, 0);

    const texture = new pc.Texture(this.app.graphicsDevice, {
      name,
      width: size,
      height: size,
      mipmaps: true,
      minFilter: pc.FILTER_LINEAR_MIPMAP_LINEAR,
      magFilter: pc.FILTER_LINEAR,
      addressU: pc.ADDRESS_REPEAT,
      addressV: pc.ADDRESS_REPEAT,
    });
    texture.setSource(canvas);
    return texture;
  }

  private createMaterials() {
    const make = (name: string, hex: string, options: { roughness?: number; metalness?: number; opacity?: number; emissive?: string } = {}) => {
      const material = new pc.StandardMaterial();
      material.name = name;
      material.diffuse = color(hex);
      material.gloss = 1 - (options.roughness ?? 0.58);
      material.metalness = options.metalness ?? 0;
      if (options.emissive) {
        material.emissive = color(options.emissive);
        material.emissiveIntensity = 1.2;
      }
      if (options.opacity !== undefined && options.opacity < 1) {
        material.opacity = options.opacity;
        material.blendType = pc.BLEND_NORMAL;
        material.depthWrite = false;
      }
      material.update();
      return material;
    };

    const floor = make('sealed_rubber_floor', '#68716b', { roughness: 0.62, metalness: 0.02 });
    floor.diffuseMap = this.createSurfaceTexture('sealed_rubber_floor_diffuse', '#68716b', '#7b847e', 'rubber');
    floor.normalMap = this.createNormalTexture('sealed_rubber_floor_normal', 'rubber');
    floor.normalMapTiling.set(10, 10);
    floor.diffuseMapTiling.set(10, 10);
    floor.bumpiness = 0.12;
    floor.update();

    const wall = make('fine_concrete_wall', '#b8ad9e', { roughness: 0.86 });
    wall.diffuseMap = this.createSurfaceTexture('fine_concrete_wall_diffuse', '#b8ad9e', '#6d665e', 'concrete');
    wall.normalMap = this.createNormalTexture('fine_concrete_wall_normal', 'concrete');
    wall.normalMapTiling.set(7, 7);
    wall.diffuseMapTiling.set(7, 7);
    wall.bumpiness = 0.18;
    wall.update();

    const wallWarm = make('warm_concrete_wall', '#d2c5af', { roughness: 0.84 });
    wallWarm.diffuseMap = this.createSurfaceTexture('warm_concrete_wall_diffuse', '#d2c5af', '#8a8072', 'concrete');
    wallWarm.normalMap = wall.normalMap;
    wallWarm.normalMapTiling.set(7, 7);
    wallWarm.diffuseMapTiling.set(7, 7);
    wallWarm.bumpiness = 0.16;
    wallWarm.update();

    const brushedPlate = make('brushed_metal_nameplate', '#b78e4d', { roughness: 0.28, metalness: 0.78 });
    brushedPlate.diffuseMap = this.createSurfaceTexture('brushed_nameplate_diffuse', '#b78e4d', '#f0cf85', 'brushed', 128);
    brushedPlate.normalMap = this.createNormalTexture('brushed_nameplate_normal', 'brushed', 128);
    brushedPlate.diffuseMapTiling.set(2, 1);
    brushedPlate.normalMapTiling.set(2, 1);
    brushedPlate.bumpiness = 0.07;
    brushedPlate.update();

    return {
      floor,
      floorLine: make('floor_line', '#151a1a', { roughness: 0.55 }),
      wall,
      wallWarm,
      wallDark: make('wall_dark', '#6f6458', { roughness: 0.76 }),
      stone: make('stone', '#b7aa96', { roughness: 0.52 }),
      rail: make('rail', '#111111', { roughness: 0.38, metalness: 0.2 }),
      light: make('light', '#fff4cf', { roughness: 0.2, emissive: '#ffe3a3' }),
      glass: make('glass', '#9bbeca', { roughness: 0.08, metalness: 0.02, opacity: 0.38 }),
      water: make('water', '#8caeb1', { roughness: 0.18, metalness: 0.04, opacity: 0.7 }),
      plant: make('plant', '#315b43', { roughness: 0.78 }),
      brass: make('brass', '#a8874e', { roughness: 0.34, metalness: 0.45 }),
      path: make('pale_rubber_walkway', '#ded8c0', { roughness: 0.66, metalness: 0.01 }),
      pathEdge: make('path_edge', '#d1b56b', { roughness: 0.36, metalness: 0.34 }),
      panel: make('art_panel', '#ddd6c8', { roughness: 0.66 }),
      mat: make('art_mat', '#f4efe5', { roughness: 0.78 }),
      frame: make('art_frame', '#151515', { roughness: 0.46, metalness: 0.05 }),
      frameMonumental: make('art_frame_monumental', '#1f1710', { roughness: 0.46, metalness: 0.05 }),
      frameMural: make('art_frame_mural', '#6e5738', { roughness: 0.46, metalness: 0.05 }),
      nameplate: brushedPlate,
      preview: make('placement_preview', '#87ff6b', { opacity: 0.28 }),
      skin: make('skin', '#c9a58e', { roughness: 0.6 }),
      darkCloth: make('dark_cloth', '#111418', { roughness: 0.72 }),
      denim: make('denim', '#1f3342', { roughness: 0.68 }),
    };
  }

  private box(name: string, size: [number, number, number], position: [number, number, number], material: pc.StandardMaterial, rotationY = 0, parent: pc.Entity = this.app.root) {
    const entity = new pc.Entity(name, this.app);
    entity.addComponent('render', { type: 'box' });
    setStandardMaterial(entity, material);
    entity.setLocalScale(size[0], size[1], size[2]);
    entity.setPosition(position[0], position[1], position[2]);
    entity.setEulerAngles(0, eulerYFromRadians(rotationY), 0);
    parent.addChild(entity);
    return entity;
  }

  private cylinder(name: string, radius: number, height: number, position: [number, number, number], material: pc.StandardMaterial, parent: pc.Entity = this.app.root) {
    const entity = new pc.Entity(name, this.app);
    entity.addComponent('render', { type: 'cylinder' });
    setStandardMaterial(entity, material);
    entity.setLocalScale(radius * 2, height, radius * 2);
    entity.setPosition(position[0], position[1], position[2]);
    parent.addChild(entity);
    return entity;
  }

  private cone(name: string, radius: number, height: number, position: [number, number, number], material: pc.StandardMaterial, parent: pc.Entity = this.app.root) {
    const entity = new pc.Entity(name, this.app);
    entity.addComponent('render', { type: 'cone' });
    setStandardMaterial(entity, material);
    entity.setLocalScale(radius * 2, height, radius * 2);
    entity.setPosition(position[0], position[1], position[2]);
    parent.addChild(entity);
    return entity;
  }

  private plane(name: string, size: [number, number], position: [number, number, number], material: pc.StandardMaterial, parent: pc.Entity) {
    const entity = new pc.Entity(name, this.app);
    entity.addComponent('render', { type: 'plane' });
    setStandardMaterial(entity, material);
    entity.setLocalScale(size[0], 1, size[1]);
    entity.setLocalEulerAngles(90, 0, 0);
    entity.setLocalPosition(position[0], position[1], position[2]);
    parent.addChild(entity);
    return entity;
  }

  private addLight(name: string, type: 'directional' | 'omni' | 'spot', position: [number, number, number], options: Record<string, unknown>) {
    const entity = new pc.Entity(name, this.app);
    entity.addComponent('light', { type, ...options });
    entity.setPosition(position[0], position[1], position[2]);
    this.app.root.addChild(entity);
    return entity;
  }

  private loadVenueAsset() {
    this.app.assets.loadFromUrl(venueAssetUrls[this.sceneVariant], 'container', (error, asset) => {
      if (error || !asset?.resource) {
        console.warn('[gallery] Blender venue unavailable, using procedural fallback.', error);
        if (this.sceneVariant === 'rdfz') {
          this.buildRdfzVenueShell();
        } else {
          this.buildVenueShell();
          this.buildForegroundVenueLandmarks();
        }
        return;
      }
      const resource = asset.resource as pc.ContainerResource;
      const entity = resource.instantiateRenderEntity({
        castShadows: true,
        receiveShadows: true,
      });
      entity.name = 'artchais_blender_venue';
      this.enhanceVenueMaterials(entity);
      this.venueEntity = entity;
      this.app.root.addChild(entity);
      if (this.sceneVariant === 'rdfz') this.buildRdfzVenueShell();
    });
  }

  private buildBaseLighting() {
    if (this.sceneVariant === 'rdfz') {
      const sun = this.addLight('rdfz_daylight_sun', 'directional', [4.5, 8.5, 9.5], {
        color: color('#fff7e8'),
        intensity: 0.85,
        castShadows: true,
        shadowResolution: 1024,
        shadowDistance: 22,
      });
      sun.lookAt(-2.5, 0, -5.5);
      this.addLight('rdfz_room_fill', 'omni', [0, 2.55, 0], {
        color: color('#f1f4f7'),
        intensity: 0.42,
        range: 24,
      });
      [
        [-7.2, 3.08, 12.8, -7.8, 1.55, 15.2],
        [0, 3.08, 12.8, 0, 1.55, 15.2],
        [7.2, 3.08, 12.8, 7.8, 1.55, 15.2],
        [-7.2, 3.08, 1.2, -9.4, 1.55, 0.2],
        [7.2, 3.08, 1.2, 9.4, 1.55, 0.2],
        [-7.2, 3.08, -12.8, -7.8, 1.55, -15.2],
        [0, 3.08, -12.8, 0, 1.55, -15.2],
        [7.2, 3.08, -12.8, 7.8, 1.55, -15.2],
      ].forEach(([x, y, z, tx, ty, tz], index) => {
        const spot = this.addLight(`rdfz_track_spot_${index}`, 'spot', [x, y, z], {
          color: color('#fff0cf'),
          intensity: 0.95,
          range: 11,
          innerConeAngle: 18,
          outerConeAngle: 38,
          castShadows: true,
          shadowResolution: 768,
          shadowDistance: 12,
        });
        spot.lookAt(tx, ty, tz);
      });
      return;
    }

    this.addLight('low_interior_fill', 'omni', [0, 4.2, -1.8], {
      color: color('#d6c3a5'),
      intensity: 1.05,
      range: 26,
    });

    indoorFixtures.forEach((fixture) => this.addFixtureLight(fixture));
  }

  private addFixtureLight(fixture: LightFixture) {
    const spot = this.addLight(`fixture_${fixture.name}`, 'spot', fixture.position, {
      color: color(fixture.color),
      intensity: fixture.intensity * 1.28,
      range: fixture.range,
      innerConeAngle: fixture.innerConeAngle,
      outerConeAngle: fixture.outerConeAngle,
      castShadows: true,
      shadowResolution: 1024,
      shadowDistance: 18,
    });
    spot.lookAt(fixture.target[0], fixture.target[1], fixture.target[2]);
    return spot;
  }

  private enhanceVenueMaterials(root: pc.Entity) {
    root.forEach((node) => {
      const entity = node as pc.Entity;
      if (!entity.render) return;
      entity.render.meshInstances.forEach((meshInstance: pc.MeshInstance) => {
        const material = meshInstance.material;
        if (!(material instanceof pc.StandardMaterial)) return;
        const name = material.name.toLowerCase();
        material.cull = pc.CULLFACE_NONE;
        if (this.sceneVariant === 'rdfz') {
          material.useLighting = true;
          if (name.includes('material_5') || name.includes('color f') || name.includes('color m00') || name.includes('color m08') || name.includes('custom')) {
            material.diffuse = color('#f4f3f0');
            material.gloss = 0.24;
            material.metalness = 0;
          } else if (name.includes('blue acrylic')) {
            material.diffuse = color('#05a86f');
            material.gloss = 0.42;
            material.metalness = 0;
            material.emissive = color('#05a86f');
            material.emissiveIntensity = 0.12;
          } else if (name.includes('color d')) {
            material.diffuse = color('#c7ad78');
            material.gloss = 0.3;
            material.metalness = 0;
          } else if (name.includes('color m04') || name.includes('material')) {
            material.diffuse = color('#b8b8b3');
            material.gloss = 0.22;
            material.metalness = 0;
          } else if (name.includes('steel') || name.includes('rdc08') || name.includes('黑')) {
            material.diffuse = color('#11100e');
            material.gloss = 0.68;
            material.metalness = 0.55;
          } else if (name.includes('glass') || name.includes('玻璃') || name.includes('color h')) {
            material.diffuse = color('#d3ecf4', 0.34);
            material.opacity = 0.34;
            material.blendType = pc.BLEND_NORMAL;
            material.depthWrite = false;
            material.gloss = 0.86;
          } else if (name.includes('acrylic') || name.includes('亞克力')) {
            material.diffuse = color('#baf4dd', 0.56);
            material.opacity = 0.56;
            material.blendType = pc.BLEND_NORMAL;
            material.depthWrite = false;
            material.gloss = 0.52;
          }
          material.update();
          return;
        }
        if (name.includes('concrete') || name.includes('plaster') || name.includes('wall')) {
          material.diffuseMap = this.materials.wall.diffuseMap;
          material.normalMap = this.materials.wall.normalMap;
          material.diffuseMapTiling.set(7, 7);
          material.normalMapTiling.set(7, 7);
          material.bumpiness = 0.16;
          material.gloss = 0.12;
          material.metalness = 0;
        } else if (name.includes('rubber') || name.includes('floor')) {
          material.diffuseMap = this.materials.floor.diffuseMap;
          material.normalMap = this.materials.floor.normalMap;
          material.diffuseMapTiling.set(10, 10);
          material.normalMapTiling.set(10, 10);
          material.bumpiness = 0.12;
          material.gloss = 0.22;
          material.metalness = 0.02;
        } else if (name.includes('steel') || name.includes('metal') || name.includes('track')) {
          material.gloss = 0.72;
          material.metalness = 0.72;
        } else if (name.includes('glass')) {
          material.opacity = 0.36;
          material.blendType = pc.BLEND_NORMAL;
          material.depthWrite = false;
          material.gloss = 0.9;
        } else if (name.includes('blue acrylic')) {
          material.emissive = color('#13b77f');
          material.emissiveIntensity = 0.18;
        } else if (name.includes('acrylic') || name.includes('glow') || name.includes('lamp')) {
          material.emissive = color('#cffff2');
          material.emissiveIntensity = 0.35;
        }
        material.update();
      });
    });
  }

  private buildRdfzVenueShell() {
    const m = this.materials;
    const { floorWidth, floorDepth, ceilingY } = rdfzVenueMetrics;
    const floor = this.box('rdfz_floor_slab', [floorWidth, 0.24, floorDepth], [0, -0.12, 0], m.floor);
    const ceiling = this.box('rdfz_ceiling_slab', [floorWidth, 0.16, floorDepth], [0, ceilingY, 0], m.wallWarm);
    if (floor.render) {
      floor.render.castShadows = false;
      floor.render.receiveShadows = true;
    }
    if (ceiling.render) {
      ceiling.render.castShadows = false;
      ceiling.render.receiveShadows = true;
    }

    for (let x = -10; x <= 10; x += 4) {
      this.box('rdfz_floor_joint_x', [0.02, 0.012, floorDepth - 1.2], [x, 0.012, 0], m.floorLine);
    }
    for (let z = -14; z <= 14; z += 4) {
      this.box('rdfz_floor_joint_z', [floorWidth - 1.2, 0.012, 0.02], [0, 0.012, z], m.floorLine);
    }

    for (const z of [-12.8, 1.2, 12.8]) {
      this.box('rdfz_ceiling_track_z', [floorWidth - 0.5, 0.06, 0.12], [0, ceilingY - 0.1, z], m.rail);
    }
    for (const x of [-7.2, 0, 7.2]) {
      this.box('rdfz_ceiling_track_x', [0.12, 0.06, floorDepth - 0.5], [x, ceilingY - 0.1, 0], m.rail);
    }
  }

  private snapToRdfzWall(x: number, z: number) {
    let best: { x: number; z: number; rotationY: number; distance: number } | null = null;
    for (const wall of rdfzWallSurfaces) {
      let distance = 0;
      let snappedX = x;
      let snappedZ = z;
      if (wall.tangent === 'x') {
        distance = Math.abs(z - wall.anchorZ);
        snappedX = clamp(x, wall.min, wall.max);
        snappedZ = wall.anchorZ;
      } else {
        distance = Math.abs(x - wall.anchorX);
        snappedZ = clamp(z, wall.min, wall.max);
        snappedX = wall.anchorX;
      }
      if (distance > 2.8) continue;
      if (!best || distance < best.distance) {
        best = { x: snappedX, z: snappedZ, rotationY: wall.rotationY, distance };
      }
    }
    return best;
  }

  private buildVenueShell() {
    const m = this.materials;
    this.box('grand_atrium_floor_slab', [64, 0.26, 46], [0, -0.13, 0], m.floor);
    this.box('entry_to_atrium_walkway', [4.4, 0.05, 34], [0, 0.045, 3.4], m.path);
    this.box('left_walkway_brass_edge', [0.08, 0.07, 34], [-2.25, 0.075, 3.4], m.pathEdge);
    this.box('right_walkway_brass_edge', [0.08, 0.07, 34], [2.25, 0.075, 3.4], m.pathEdge);
    this.box('wide_entry_floor_band', [42, 0.045, 3.2], [0, 0.05, 17.6], m.path);
    this.box('atrium_floor_band', [28, 0.045, 3.2], [0, 0.052, -2.2], m.path);
    this.box('entry_portal_left_pier', [0.44, 3.8, 0.44], [-2.85, 1.9, 12.6], m.wallDark);
    this.box('entry_portal_right_pier', [0.44, 3.8, 0.44], [2.85, 1.9, 12.6], m.wallDark);
    this.box('entry_portal_overhead_beam', [6.1, 0.34, 0.42], [0, 3.85, 12.6], m.wallDark);
    this.box('entry_portal_floor_lip', [7.2, 0.16, 0.55], [0, 0.16, 12.6], m.pathEdge);

    for (let x = -28; x <= 28; x += 4) this.box('floor_control_joint_x', [0.025, 0.018, 42], [x, 0.012, 0], m.floorLine);
    for (let z = -20; z <= 20; z += 4) this.box('floor_control_joint_z', [60, 0.018, 0.025], [0, 0.014, z], m.floorLine);

    this.box('north_monumental_wall', [62, 8.2, 0.72], [0, 4.1, -21.6], m.wallWarm);
    this.box('west_perimeter_wall', [0.72, 7.4, 43], [-31.2, 3.7, -0.4], m.wall);
    this.box('east_perimeter_wall', [0.72, 7.4, 43], [31.2, 3.7, -0.4], m.wall);
    this.box('south_entry_wall_left', [21, 5.4, 0.68], [-20.5, 2.7, 21.6], m.wallDark);
    this.box('south_entry_wall_right', [21, 5.4, 0.68], [20.5, 2.7, 21.6], m.wallDark);
    this.box('south_entry_header', [18, 1.4, 0.72], [0, 5.8, 21.6], m.wallDark);
    this.box('left_long_gallery_wall', [0.68, 5.8, 25], [-11.4, 2.9, -4.1], m.wallDark);
    this.box('right_long_gallery_wall', [0.68, 5.8, 25], [11.4, 2.9, -4.1], m.wallDark);
    this.box('left_front_gallery_wall', [13.2, 5.3, 0.64], [-10.2, 2.65, 9.2], m.wallWarm);
    this.box('right_front_gallery_wall', [13.2, 5.3, 0.64], [10.2, 2.65, 9.2], m.wallWarm);
    this.box('left_diagonal_gallery_wall', [12.4, 5.3, 0.64], [-22.2, 2.65, 5.2], m.wallDark, -0.44);
    this.box('right_diagonal_gallery_wall', [12.4, 5.3, 0.64], [22.2, 2.65, 5.2], m.wallDark, 0.44);
    this.box('rear_cross_gallery_wall', [18, 6.2, 0.7], [0, 3.1, -10.8], m.wall);

    for (const [x, z] of [
      [-26, -17.6], [26, -17.6], [-26, 15.8], [26, 15.8],
      [-6.8, -2.2], [6.8, -2.2], [-15.8, 10.8], [15.8, 10.8],
    ] as const) {
      this.cylinder('round_structural_column', 0.42, 7.1, [x, 3.55, z], m.stone);
      this.cylinder('round_column_capital', 0.52, 0.22, [x, 7.18, z], m.stone);
    }

    this.cylinder('central_sunken_atrium_ring', 8.0, 0.5, [0, 0.25, -2.2], m.stone);
    this.cylinder('central_reflecting_pool', 3.45, 0.28, [0, 0.66, -2.2], m.water);
    for (let i = 0; i < 5; i += 1) {
      const ring = this.cylinder('central_black_ring_sculpture', 0.72 + i * 0.23, 0.04, [0, 1.12 + i * 0.22, -2.2], m.rail);
      ring.setEulerAngles(68, i * 40, i * 22);
    }
    this.box('sealed_acoustic_ceiling', [64, 0.34, 46], [0, 8.04, 0], m.wallWarm);
    const ceilingDisc = this.cylinder('closed_atrium_ceiling_disc', 9.2, 0.06, [0, 7.82, -2.2], m.wall);
    ceilingDisc.setLocalScale(18.4, 0.06, 18.4);

    for (const x of [-22, -11, 0, 11, 22]) this.box('roof_transverse_beam', [0.18, 0.28, 38], [x, 7.88, -1.2], m.rail);
    for (const z of [-18, -10, -2, 6, 14]) this.box('roof_longitudinal_beam', [58, 0.24, 0.16], [0, 7.9, z], m.rail);

    this.rail('left_gallery_track', -11.4, 6.25, -4.1, Math.PI / 2, 22, 7);
    this.rail('right_gallery_track', 11.4, 6.25, -4.1, Math.PI / 2, 22, 7);
    this.rail('north_monumental_track', 0, 6.45, -20.65, 0, 34, 8);
    this.rail('front_gallery_track', 0, 6.05, 9.2, 0, 24, 7);

    for (const [x, z] of [[-5.8, 3.8], [5.8, 3.8], [-24.5, -12.4], [24.5, -12.4], [-24.5, 13.5], [24.5, 13.5]] as const) {
      this.planter(x, z);
    }
  }

  private rail(name: string, x: number, y: number, z: number, rotationY: number, length: number, spots = 6) {
    this.box(`${name}_rail`, [length, 0.07, 0.08], [x, y, z], this.materials.rail, rotationY);
    for (let i = 0; i < spots; i += 1) {
      const t = spots === 1 ? 0 : i / (spots - 1) - 0.5;
      const lamp = this.cylinder(`${name}_spot_light_head`, 0.16, 0.28, [
        x + Math.cos(rotationY) * t * length,
        y - 0.26,
        z - Math.sin(rotationY) * t * length,
      ], this.materials.light);
      lamp.setEulerAngles(90, eulerYFromRadians(rotationY), 0);
    }
  }

  private planter(x: number, z: number) {
    this.cylinder('stone_planter', 0.9, 0.5, [x, 0.25, z], this.materials.stone);
    for (let i = 0; i < 10; i += 1) {
      const angle = (i / 10) * Math.PI * 2;
      const leaf = this.cone('planter_leaf', 0.14, 0.86, [
        x + Math.cos(angle) * 0.32,
        0.92,
        z + Math.sin(angle) * 0.32,
      ], this.materials.plant);
      leaf.setEulerAngles(45, -angle * radToDeg, 0);
    }
  }

  private buildForegroundVenueLandmarks() {
    const m = this.materials;
    this.box('static_entry_floor_visible', [8.6, 0.22, 7.2], [0, 0.12, 15.2], m.path);
    this.box('static_entry_left_brass_edge', [0.08, 0.1, 8.4], [-2.28, 0.24, 14.65], m.brass);
    this.box('static_entry_right_brass_edge', [0.08, 0.1, 8.4], [2.28, 0.24, 14.65], m.brass);
    this.box('static_entry_left_pier_visible', [0.68, 4.65, 0.82], [-3.72, 2.35, 14.25], m.wallDark);
    this.box('static_entry_right_pier_visible', [0.68, 4.65, 0.82], [3.72, 2.35, 14.25], m.wallDark);
    this.box('static_entry_header_visible', [8.1, 0.5, 0.88], [0, 4.62, 14.25], m.wallDark);
    this.box('static_entry_threshold_visible', [8.8, 0.18, 0.85], [0, 0.28, 17.25], m.brass);
    this.box('static_ceiling_track_visible', [12, 0.08, 0.12], [0, 5.72, 11.9], m.rail);
    this.box('static_left_spot_visible', [0.34, 0.18, 0.34], [-2.35, 5.52, 11.9], m.light);
    this.box('static_right_spot_visible', [0.34, 0.18, 0.34], [2.35, 5.52, 11.9], m.light);
  }

  private makeSlots(): Slot[] {
    if (this.sceneVariant === 'rdfz') {
      const { outerWallX, outerWallZ } = rdfzVenueMetrics;
      return [
        ...[-8.8, -5.8, -2.8, 0.2, 3.2, 6.2, 8.8].map((x) => ({ x, z: -outerWallZ, rotationY: 0, kind: 'wall' as FrameType })),
        ...[-8.6, -5.6, -2.6, 0.4, 3.4, 6.4, 8.6].map((x) => ({ x, z: outerWallZ, rotationY: Math.PI, kind: 'wall' as FrameType })),
        ...[-10.8, -8.2, -5.6, -2.0, 1.6, 5.2, 8.8, 10.8].map((z) => ({ x: -outerWallX, z, rotationY: Math.PI / 2, kind: 'mural' as FrameType })),
        ...[-10.8, -8.2, -5.6, -2.0, 1.6, 5.2, 8.8, 10.8].map((z) => ({ x: outerWallX, z, rotationY: -Math.PI / 2, kind: 'mural' as FrameType })),
        ...[-7.8, -4.8, -1.8, 1.2, 4.2, 7.2].map((x) => ({ x, z: -3.8, rotationY: 0, kind: 'wall' as FrameType })),
        ...[-7.8, -4.8, -1.8, 1.2, 4.2, 7.2].map((x) => ({ x, z: 7.8, rotationY: Math.PI, kind: 'wall' as FrameType })),
        ...[-8, -4, 0, 4, 8].map((x) => ({ x, z: -9.5, rotationY: Math.PI, kind: 'shelf' as FrameType })),
      ];
    }
    return [
      ...[-24, -18, -12, -6, 0, 6, 12, 18, 24].map((x) => ({ x, z: -21.05, rotationY: 0, kind: 'monumental' as FrameType })),
      ...[-17, -13, -9, -5, -1, 3, 7].map((z) => ({ x: -11.02, z, rotationY: Math.PI / 2, kind: 'wall' as FrameType })),
      ...[-17, -13, -9, -5, -1, 3, 7].map((z) => ({ x: 11.02, z, rotationY: -Math.PI / 2, kind: 'wall' as FrameType })),
      ...[-26, -21, -16, -11].map((x) => ({ x, z: -12.98, rotationY: 0, kind: 'wall' as FrameType })),
      ...[11, 16, 21, 26].map((x) => ({ x, z: -12.98, rotationY: 0, kind: 'wall' as FrameType })),
      ...[-18, -13, -8, -3, 2, 7, 12, 17].map((z) => ({ x: -30.82, z, rotationY: Math.PI / 2, kind: 'mural' as FrameType })),
      ...[-18, -13, -8, -3, 2, 7, 12, 17].map((z) => ({ x: 30.82, z, rotationY: -Math.PI / 2, kind: 'mural' as FrameType })),
      ...[-24, -18, -12, -6, 6, 12, 18, 24].map((x) => ({ x, z: 20.95, rotationY: Math.PI, kind: 'shelf' as FrameType })),
    ];
  }

  private placeArtworks(artworks: GalleryArtwork[], placements: ArtworkPlacement[] = [], autoPlace = true) {
    this.artworkObjects.splice(0).forEach((item) => item.entity.destroy());
    this.artworkLightLayer.children.slice().forEach((child) => child.destroy());

    if (placements.length > 0) {
      placements.forEach((placement) => this.addPlacement(placement));
      return;
    }
    if (!autoPlace) return;

    const slots = this.makeSlots();
    const sorted = [...artworks].sort((a, b) => {
      const score = (item: GalleryArtwork) => (item.frameType === 'monumental' ? 0 : item.frameType === 'mural' ? 1 : 2);
      return score(a) - score(b);
    });

    sorted.slice(0, slots.length).forEach((artwork, index) => {
      const slot = slots[index % slots.length];
      const group = this.createArtworkGroup(artwork, slot.kind);
      group.tags.add(`placement:${artwork.id}`);
      const hangY = this.sceneVariant === 'rdfz' ? rdfzVenueMetrics.hangY : 2.55;
      const logicalPosition = new pc.Vec3(slot.x, slot.kind === 'shelf' ? 1.08 : hangY, slot.z);
      this.setArtworkTransform(group, logicalPosition, slot.rotationY);
      this.artworkLayer.addChild(group);
      this.artworkObjects.push({ artwork, entity: group, position: logicalPosition, kind: slot.kind });
      this.addSpotForArtwork(group.getPosition(), slot.rotationY, slot.kind);
    });
  }

  private addPlacement(placement: ArtworkPlacement) {
    const artwork = this.artworkCatalog.find((item) => item.id === (placement.artworkId || placement.id));
    if (!artwork) return;
    const group = this.createArtworkGroup(artwork, placement.kind);
    group.tags.add(`placement:${placement.id}`);
    const logicalPosition = new pc.Vec3(placement.x, placement.y, placement.z);
    this.setArtworkTransform(group, logicalPosition, placement.rotationY);
    group.setLocalScale(placement.scale, placement.scale, placement.scale);
    this.artworkLayer.addChild(group);
    this.artworkObjects.push({ artwork, entity: group, position: logicalPosition, kind: placement.kind });
    this.addSpotForArtwork(group.getPosition(), placement.rotationY, placement.kind);
    this.emitSelection(this.artworkObjects[this.artworkObjects.length - 1]);
  }

  private makePreviewPlacement(artwork: GalleryArtwork): Omit<ArtworkPlacement, 'id' | 'artworkId' | 'title'> {
    const placementRotation = this.yaw + this.previewRotationOffset;
    const forward = new pc.Vec3(Math.sin(placementRotation), 0, -Math.cos(placementRotation));
    const position = this.resolveCollision(this.playerPosition.clone().add(forward.mulScalar(3.2)));
    const kind = artwork.frameType === 'shelf' ? 'wall' : artwork.frameType;
    const hangY = this.sceneVariant === 'rdfz' ? rdfzVenueMetrics.hangY : 2.35;
    let rotationY = placementRotation;
    let x = position.x;
    let z = position.z;

    if (this.sceneVariant === 'rdfz') {
      const snapped = this.snapToRdfzWall(position.x, position.z);
      if (snapped) {
        x = snapped.x;
        z = snapped.z;
        rotationY = snapped.rotationY;
      }
    }

    return {
      x: Number(x.toFixed(2)),
      y: hangY,
      z: Number(z.toFixed(2)),
      rotationY: Number(rotationY.toFixed(3)),
      scale: 1,
      kind,
      createdIn: 'playcanvas',
    };
  }

  private createArtworkGroup(artwork: GalleryArtwork, frameType: FrameType) {
    const group = new pc.Entity(`artwork_${artwork.id}`, this.app);
    const maxWidth = frameType === 'monumental' ? 4.7 : frameType === 'mural' ? 3.45 : frameType === 'shelf' ? 1.35 : 2.35;
    const maxHeight = frameType === 'monumental' ? 3.2 : frameType === 'mural' ? 2.35 : frameType === 'shelf' ? 1.05 : 1.8;
    let width = maxWidth;
    let height = width / artwork.aspect;
    if (height > maxHeight) {
      height = maxHeight;
      width = height * artwork.aspect;
    }
    const frameDepth = frameType === 'shelf' ? 0.07 : 0.1;
    const frameThickness = frameType === 'monumental' ? 0.12 : frameType === 'shelf' ? 0.055 : 0.08;
    const frameMaterial = frameType === 'monumental' ? this.materials.frameMonumental : frameType === 'mural' ? this.materials.frameMural : this.materials.frame;
    this.box('artwork_frame_top', [width + frameThickness * 2, frameThickness, frameDepth], [0, height / 2 + frameThickness / 2, 0], frameMaterial, 0, group);
    this.box('artwork_frame_bottom', [width + frameThickness * 2, frameThickness, frameDepth], [0, -height / 2 - frameThickness / 2, 0], frameMaterial, 0, group);
    this.box('artwork_frame_left', [frameThickness, height, frameDepth], [-width / 2 - frameThickness / 2, 0, 0], frameMaterial, 0, group);
    this.box('artwork_frame_right', [frameThickness, height, frameDepth], [width / 2 + frameThickness / 2, 0, 0], frameMaterial, 0, group);

    const faceMaterial = new pc.StandardMaterial();
    faceMaterial.name = `artwork_texture_${artwork.id}`;
    faceMaterial.diffuse = color('#f4efe5');
    faceMaterial.useLighting = false;
    faceMaterial.update();
    this.loadTextureIntoMaterial(artwork.imageUrl, faceMaterial);
    const face = this.plane('artwork_image', [width, height], [0, 0, 0.064], faceMaterial, group);
    face.tags.add(`artwork:${artwork.id}`);

    if (frameType === 'shelf') {
      this.box('artwork_shelf', [width + 0.5, 0.14, 0.58], [0, -height / 2 - 0.23, -0.05], this.materials.mat, 0, group);
    }

    const label = this.makeLabel(artwork.title, artwork.artistName, artwork.artistAvatar);
    label.setLocalPosition(0, -height / 2 - (frameType === 'shelf' ? 0.48 : 0.32), 0.075);
    group.addChild(label);
    this.setArtworkRenderFlags(group);
    return group;
  }

  private loadTextureIntoMaterial(url: string, material: pc.StandardMaterial) {
    void this.loadTexture(url).then((texture) => {
      material.diffuseMap = texture;
      material.update();
    }).catch((error) => {
      console.warn('[gallery] Artwork texture failed to load.', url, error);
    });
  }

  private loadTexture(url: string): Promise<pc.Texture> {
    if (this.disposed) return Promise.reject(new Error('Scene disposed'));
    const cached = this.textureCache.get(url);
    if (cached) return Promise.resolve(cached);
    const inflight = this.textureInflight.get(url);
    if (inflight) return inflight;

    const promise = new Promise<pc.Texture>((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        if (this.disposed) {
          this.textureInflight.delete(url);
          reject(new Error('Scene disposed'));
          return;
        }
        const texture = new pc.Texture(this.app.graphicsDevice, {
          name: url,
          width: image.width,
          height: image.height,
          mipmaps: true,
          minFilter: pc.FILTER_LINEAR,
          magFilter: pc.FILTER_LINEAR,
          addressU: pc.ADDRESS_CLAMP_TO_EDGE,
          addressV: pc.ADDRESS_CLAMP_TO_EDGE,
          flipY: false,
        });
        texture.setSource(image);
        this.textureInflight.delete(url);
        this.textureCache.set(url, texture);
        resolve(texture);
      };
      image.onerror = () => {
        this.textureInflight.delete(url);
        reject(new Error('Image request failed'));
      };
      image.src = url;
    });
    this.textureInflight.set(url, promise);
    return promise;
  }

  private rebuildPlacementPreview() {
    this.previewLayer.children.slice().forEach((child) => child.destroy());
    if (!this.builderMode || !this.previewEnabled || !this.previewArtworkId) return;
    const artwork = this.artworkCatalog.find((item) => item.id === this.previewArtworkId);
    if (!artwork) return;
    const kind = artwork.frameType === 'shelf' ? 'wall' : artwork.frameType;
    const maxWidth = kind === 'monumental' ? 4.7 : kind === 'mural' ? 3.45 : 2.35;
    const maxHeight = kind === 'monumental' ? 3.2 : kind === 'mural' ? 2.35 : 1.8;
    let width = maxWidth;
    let height = width / artwork.aspect;
    if (height > maxHeight) {
      height = maxHeight;
      width = height * artwork.aspect;
    }
    const ghost = new pc.Entity('placement_preview', this.app);
    const thickness = 0.08;
    this.box('placement_preview_frame_top', [width + thickness * 2, thickness, 0.08], [0, height / 2 + thickness / 2, 0], this.materials.preview, 0, ghost);
    this.box('placement_preview_frame_bottom', [width + thickness * 2, thickness, 0.08], [0, -height / 2 - thickness / 2, 0], this.materials.preview, 0, ghost);
    this.box('placement_preview_frame_left', [thickness, height, 0.08], [-width / 2 - thickness / 2, 0, 0], this.materials.preview, 0, ghost);
    this.box('placement_preview_frame_right', [thickness, height, 0.08], [width / 2 + thickness / 2, 0, 0], this.materials.preview, 0, ghost);
    this.plane('placement_preview_face', [width, height], [0, 0, 0.045], this.materials.preview, ghost);
    this.previewLayer.addChild(ghost);
    this.updatePlacementPreview();
  }

  private updatePlacementPreview() {
    if (!this.builderMode || !this.previewEnabled || this.previewLayer.children.length === 0) return;
    const artwork = this.artworkCatalog.find((item) => item.id === this.previewArtworkId);
    if (!artwork) return;
    const placement = this.makePreviewPlacement(artwork);
    const ghost = this.previewLayer.children[0];
    ghost.setPosition(placement.x, placement.y, placement.z);
    ghost.setEulerAngles(0, eulerYFromRadians(placement.rotationY), 0);
    ghost.setLocalScale(placement.scale, placement.scale, placement.scale);
  }

  private makeLabel(title: string, artistName: string, artistAvatar?: string) {
    const holder = new pc.Entity('label_holder', this.app);
    const canvas = document.createElement('canvas');
    canvas.width = 768;
    canvas.height = 160;
    const ctx = canvas.getContext('2d')!;
    const drawLabel = (avatar?: HTMLImageElement) => {
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, '#d2ad64');
      gradient.addColorStop(0.46, '#f1d58d');
      gradient.addColorStop(1, '#a87931');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = 0.22;
      ctx.strokeStyle = '#fff1bd';
      for (let y = 12; y < canvas.height; y += 6) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y + 2);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;

      ctx.save();
      ctx.beginPath();
      ctx.arc(78, 80, 44, 0, Math.PI * 2);
      ctx.clip();
      if (avatar) {
        ctx.drawImage(avatar, 34, 36, 88, 88);
      } else {
        ctx.fillStyle = '#2b2116';
        ctx.fillRect(34, 36, 88, 88);
        ctx.fillStyle = '#f4d990';
        ctx.font = '700 42px Inter, Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText((artistName.trim()[0] || 'A').toUpperCase(), 78, 82);
      }
      ctx.restore();
      ctx.lineWidth = 4;
      ctx.strokeStyle = '#322415';
      ctx.beginPath();
      ctx.arc(78, 80, 44, 0, Math.PI * 2);
      ctx.stroke();

      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = '#20160d';
      ctx.font = '800 40px Inter, Arial';
      ctx.fillText(title.slice(0, 26), 146, 66);
      ctx.fillStyle = '#4a3218';
      ctx.font = '600 27px Inter, Arial';
      ctx.fillText(artistName.slice(0, 30), 146, 112);
    };

    drawLabel();

    const texture = new pc.Texture(this.app.graphicsDevice, {
      width: canvas.width,
      height: canvas.height,
      addressU: pc.ADDRESS_CLAMP_TO_EDGE,
      addressV: pc.ADDRESS_CLAMP_TO_EDGE,
      flipY: false,
    });
    texture.setSource(canvas);
    const material = new pc.StandardMaterial();
    material.useLighting = false;
    material.diffuseMap = texture;
    material.opacity = 1;
    material.update();
    this.box('artwork_nameplate_metal', [1.95, 0.36, 0.035], [0, 0, -0.012], this.materials.nameplate, 0, holder);
    this.plane('artwork_label_text', [1.82, 0.28], [0, 0, 0.012], material, holder);

    if (artistAvatar) {
      const avatar = new Image();
      avatar.crossOrigin = 'anonymous';
      avatar.onload = () => {
        drawLabel(avatar);
        texture.setSource(canvas);
        material.update();
      };
      avatar.src = artistAvatar;
    }

    return holder;
  }

  private addSpotForArtwork(position: pc.Vec3, rotationY: number, frameType: FrameType) {
    const offset = new pc.Vec3(Math.sin(rotationY) * 1.4, 0, Math.cos(rotationY) * 1.4);
    const spot = new pc.Entity('artwork_spot_light', this.app);
    spot.addComponent('light', {
      type: 'spot',
      color: color('#ffe3b4'),
      intensity: frameType === 'monumental' ? 2.65 : 1.85,
      range: 8.5,
      innerConeAngle: frameType === 'shelf' ? 18 : 13,
      outerConeAngle: frameType === 'shelf' ? 31 : 24,
      castShadows: true,
      shadowResolution: 768,
    });
    const spotY = this.sceneVariant === 'rdfz' ? rdfzVenueMetrics.ceilingY - 0.34 : 5.74;
    spot.setPosition(position.x + offset.x, spotY, position.z + offset.z);
    spot.lookAt(position.x, position.y, position.z);
    this.artworkLightLayer.addChild(spot);
  }

  private buildVisitors() {
    const visitorPositions = [
      [-7.3, 11.4],
      [-3.6, 6.5],
      [3.1, 3.4],
      [-13, -2],
      [13, -7.5],
      [-4.2, -7.5],
      [6.8, -10.4],
    ];
    visitorPositions.forEach(([x, z], index) => {
      const visitor = new pc.Entity(`visitor_${visitorNames[index] || 'Guest'}`, this.app);
      this.capsulePerson(visitor, index % 2 ? this.materials.rail : this.materials.wallDark);
      const position = this.resolveCollision(new pc.Vec3(x, 0, z));
      visitor.setPosition(position.x, 0, position.z);
      visitor.setEulerAngles(0, Math.atan2(-position.x, -position.z) * radToDeg, 0);
      this.app.root.addChild(visitor);
    });
  }

  private buildProtagonist() {
    const human = new pc.Entity('builder_protagonist', this.app);
    this.capsulePerson(human, this.materials.darkCloth);
    human.setPosition(this.playerPosition.x, 0.02, this.playerPosition.z);
    human.setLocalScale(1.08, 1.08, 1.08);
    this.protagonist = human;
    this.app.root.addChild(human);
  }

  private capsulePerson(parent: pc.Entity, bodyMaterial: pc.StandardMaterial) {
    const body = this.cylinder('body', 0.2, 0.78, [0, 0.95, 0], bodyMaterial, parent);
    body.setLocalScale(0.4, 0.78, 0.32);
    const head = new pc.Entity('head', this.app);
    head.addComponent('render', { type: 'sphere' });
    setStandardMaterial(head, this.materials.skin);
    head.setLocalScale(0.34, 0.34, 0.34);
    head.setLocalPosition(0, 1.55, 0);
    parent.addChild(head);
  }

  private bindEvents(canvas: HTMLCanvasElement) {
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    window.addEventListener('resize', this.handleResize);
    canvas.addEventListener('pointerdown', this.handlePointerDown);
    canvas.addEventListener('pointermove', this.handlePointerMove);
    canvas.addEventListener('pointerup', this.handlePointerUp);
    canvas.addEventListener('click', this.handleClick);
    canvas.addEventListener('contextmenu', this.handleContextMenu);
  }

  private handleResize = () => {
    this.app.resizeCanvas();
    this.app.updateCanvasSize();
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    if (this.builderMode && this.previewEnabled && event.key.toLowerCase() === 'r') {
      event.preventDefault();
      this.rotatePlacementPreview(event.shiftKey ? -1 : 1);
    }
    this.keys.add(event.key.toLowerCase());
  };

  private handleKeyUp = (event: KeyboardEvent) => {
    this.keys.delete(event.key.toLowerCase());
  };

  private handlePointerDown = (event: PointerEvent) => {
    this.dragging = true;
    this.lastPointer = { x: event.clientX, y: event.clientY };
    this.app.graphicsDevice.canvas.setPointerCapture(event.pointerId);
  };

  private handlePointerMove = (event: PointerEvent) => {
    if (!this.dragging) return;
    const dx = event.clientX - this.lastPointer.x;
    const dy = event.clientY - this.lastPointer.y;
    this.yaw -= dx * 0.0032;
    this.pitch = clamp(this.pitch - dy * 0.0025, -0.78, 0.68);
    this.lastPointer = { x: event.clientX, y: event.clientY };
  };

  private handlePointerUp = (event: PointerEvent) => {
    this.dragging = false;
    if (this.app.graphicsDevice.canvas.hasPointerCapture(event.pointerId)) {
      this.app.graphicsDevice.canvas.releasePointerCapture(event.pointerId);
    }
  };

  private handleClick = (event: MouseEvent) => {
    if (this.builderMode && this.previewEnabled) {
      this.rotatePlacementPreview(1);
    }
    if (this.builderMode) return;
    const rect = this.app.graphicsDevice.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const hit = this.findArtworkByScreenPoint(x, y);
    if (hit) this.emitSelection(hit);
  };

  private handleContextMenu = (event: MouseEvent) => {
    if (!this.builderMode || !this.previewEnabled || !this.previewArtworkId) return;
    event.preventDefault();
    const placement = this.addFrameFromArtwork(this.previewArtworkId);
    if (placement) this.onPlace?.(placement);
  };

  private update = (delta: number) => {
    this.updateMovement(Math.min(delta, 0.04));
    this.updatePlacementPreview();
    this.updateFocusedArtwork();
  };

  private updateMovement(delta: number) {
    if (this.keys.has('arrowleft')) this.yaw += delta * 1.65;
    if (this.keys.has('arrowright')) this.yaw -= delta * 1.65;
    if (this.keys.has('arrowup')) this.pitch = clamp(this.pitch + delta * 1.05, -0.78, 0.68);
    if (this.keys.has('arrowdown')) this.pitch = clamp(this.pitch - delta * 1.05, -0.78, 0.68);

    const speed = this.keys.has('shift') ? 5.8 : 3.1;
    this.camera.setEulerAngles(this.pitch * radToDeg, this.yaw * radToDeg, 0);
    const forward = this.camera.forward.clone();
    forward.y = 0;
    if (forward.lengthSq() > 0) forward.normalize();
    const right = this.camera.right.clone();
    right.y = 0;
    if (right.lengthSq() > 0) right.normalize();
    const move = new pc.Vec3();
    if (this.keys.has('w')) move.add(forward);
    if (this.keys.has('s')) move.sub(forward);
    if (this.keys.has('d')) move.add(right);
    if (this.keys.has('a')) move.sub(right);

    if (this.builderMode) {
      if (move.lengthSq() > 0) {
        move.normalize().mulScalar(speed * delta);
        this.playerPosition.copy(this.resolveCollision(this.playerPosition.clone().add(move)));
      }
      if (this.protagonist) {
        this.protagonist.setPosition(this.playerPosition.x, 0.02, this.playerPosition.z);
        this.protagonist.setEulerAngles(0, eulerYFromRadians(this.yaw), 0);
      }
      const followDistance = 5.6;
      const cameraHeight = 2.8 + Math.max(0, -this.pitch) * 2.2;
      const desiredCamera = this.playerPosition.clone().sub(forward.clone().mulScalar(followDistance));
      desiredCamera.y = cameraHeight;
      const current = this.camera.getPosition();
      current.lerp(current, desiredCamera, Math.min(1, delta * 9));
      this.camera.setPosition(current);
      this.camera.lookAt(this.playerPosition.x, 1.3, this.playerPosition.z);
      this.onMove({ x: this.playerPosition.x, z: this.playerPosition.z, yaw: this.yaw });
      return;
    }

    if (move.lengthSq() > 0) {
      move.normalize().mulScalar(speed * delta);
      const current = this.camera.getPosition();
      const next = this.resolveCollision(current.clone().add(move));
      next.y = current.y;
      this.camera.setPosition(next);
    }
    const position = this.camera.getPosition();
    this.onMove({ x: position.x, z: position.z, yaw: this.yaw });
  }

  private resolveCollision(target: pc.Vec3) {
    if (this.sceneVariant === 'rdfz') {
      target.x = clamp(target.x, -10.8, 10.8);
      target.z = clamp(target.z, -16.2, 16.2);
      return target;
    }

    target.x = clamp(target.x, -28.5, 28.5);
    target.z = clamp(target.z, -19.5, 20.3);

    const radius = 0.34;
    secretCollisionCircles.forEach((circle) => this.resolveCircleCollision(target, circle, radius));
    secretCollisionRects.forEach((rect) => this.resolveRectCollision(target, rect, radius));
    return target;
  }

  private resolveCircleCollision(target: pc.Vec3, circle: CollisionCircle, radius: number) {
    const dx = target.x - circle.x;
    const dz = target.z - circle.z;
    const minDistance = circle.radius + radius;
    const distance = Math.sqrt(dx * dx + dz * dz);
    if (distance >= minDistance) return;
    const scale = distance > 0.0001 ? minDistance / distance : minDistance;
    target.x = circle.x + (distance > 0.0001 ? dx * scale : minDistance);
    target.z = circle.z + (distance > 0.0001 ? dz * scale : 0);
  }

  private resolveRectCollision(target: pc.Vec3, rect: CollisionRect, radius: number) {
    const rotationY = rect.rotationY || 0;
    const cos = Math.cos(-rotationY);
    const sin = Math.sin(-rotationY);
    const dx = target.x - rect.x;
    const dz = target.z - rect.z;
    let localX = dx * cos - dz * sin;
    let localZ = dx * sin + dz * cos;
    if (Math.abs(localX) >= rect.halfX + radius || Math.abs(localZ) >= rect.halfZ + radius) return;

    const pushX = rect.halfX + radius - Math.abs(localX);
    const pushZ = rect.halfZ + radius - Math.abs(localZ);
    if (pushX < pushZ) localX = Math.sign(localX || 1) * (rect.halfX + radius);
    else localZ = Math.sign(localZ || 1) * (rect.halfZ + radius);

    const worldCos = Math.cos(rotationY);
    const worldSin = Math.sin(rotationY);
    target.x = rect.x + localX * worldCos - localZ * worldSin;
    target.z = rect.z + localX * worldSin + localZ * worldCos;
  }

  private findArtworkByScreenPoint(x: number, y: number, maxScreenDistance = 72) {
    if (!this.camera.camera) return undefined;
    const canvas = this.app.graphicsDevice.canvas as HTMLCanvasElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width / canvas.width;
    const scaleY = rect.height / canvas.height;
    const cameraPosition = this.camera.getPosition();
    const candidates = this.artworkObjects
      .map((item) => {
        const screen = this.camera.camera!.worldToScreen(item.entity.getPosition());
        const screenX = screen.x * scaleX;
        const screenY = screen.y * scaleY;
        const dx = screenX - x;
        const dy = screenY - y;
        return {
          item,
          distance: Math.sqrt(dx * dx + dy * dy),
          worldDistance: distance2d(item.entity.getPosition(), cameraPosition),
          z: screen.z,
        };
      })
      .filter((candidate) => candidate.z > 0 && candidate.distance < maxScreenDistance && candidate.worldDistance < 12)
      .sort((a, b) => a.distance - b.distance);
    return candidates[0]?.item;
  }

  private findArtworkByPlacementId(id: string) {
    return this.artworkObjects.find((item) => this.getPlacementId(item.entity) === id);
  }

  private getPlacementId(entity: pc.Entity) {
    return entity.tags.list().find((tag) => tag.startsWith('placement:'))?.slice('placement:'.length) || '';
  }

  private emitSelection(item: ArtworkObject) {
    this.onSelect({
      id: item.artwork.id,
      title: item.artwork.title,
      artistName: item.artwork.artistName,
      description: item.artwork.description,
      imageUrl: item.artwork.imageUrl,
      sourceUrl: item.artwork.sourceUrl,
      frameType: item.artwork.frameType,
      price: item.artwork.price,
      currency: item.artwork.currency,
      position: { x: item.position.x, z: item.position.z },
    });
  }

  private updateFocusedArtwork() {
    const canvas = this.app.graphicsDevice.canvas as HTMLCanvasElement;
    const rect = canvas.getBoundingClientRect();
    const hit = this.findArtworkByScreenPoint(rect.width / 2, rect.height / 2, 58);
    const nextId = hit?.artwork.id || '';
    if (nextId === this.focusedArtworkId) return;
    this.focusedArtworkId = nextId;
    if (hit) this.emitSelection(hit);
    else this.onSelect(null);
  }

  private setArtworkTransform(entity: pc.Entity, logicalPosition: pc.Vec3, rotationY: number) {
    const displayPosition = logicalPosition.clone().add(new pc.Vec3(
      Math.sin(rotationY) * artworkWallOffset,
      0,
      Math.cos(rotationY) * artworkWallOffset,
    ));
    entity.setPosition(displayPosition);
    entity.setEulerAngles(0, eulerYFromRadians(rotationY), 0);
  }

  private setArtworkRenderFlags(entity: pc.Entity) {
    if (entity.render) {
      entity.render.castShadows = false;
      entity.render.receiveShadows = false;
    }
    entity.children.forEach((child) => this.setArtworkRenderFlags(child as pc.Entity));
  }
}
